// src/pages/ForgotPassword.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import ErrorMessage from "../components/ErrorMessage";
import SuccessMessage from "../components/SuccessMessage";
import { API_BASE } from "../config";
import { sanitizeAndValidateEmail } from "../utils/inputValidation";
import { getUserFriendlyError } from "../utils/errorMessages";

export default function ForgotPassword() {
  const blue = "#3366CC";
  const text = "#0F172A";
  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const inputRef = useRef(null);

  const responsiveCSS = `
  * { box-sizing: border-box; }
  
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }

  .page-container {
    width: 100% !important;
    overflow-x: hidden;
    background: #fff;
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }

    .desktop-nav { 
      display: none !important; 
    }

    .hamburger-btn { 
      display: inline-flex !important; 
    }

    .sticky-nav {
      height: 60px !important;
      padding: 0 16px !important;
    }
    
    .grid-2 { 
      grid-template-columns: 1fr !important; 
      gap: 24px !important; 
    }

    .account-menu-panel {
      right: 10px !important;
      top: 50px !important;
      width: calc(100vw - 20px) !important;
      max-width: 300px !important;
    }

    .hero { 
      padding: 60px 20px !important; 
      min-height: auto !important;
    }

    .hero-title { 
      font-size: 32px !important; 
      text-align: center !important; 
      width: 100%;
    }

    .cta-buttons { 
      flex-direction: column !important; 
      width: 100% !important; 
      gap: 12px !important;
    }

    .cta-buttons > button { 
      width: 100% !important; 
      margin: 0 !important;
    }

    .what-is, #why-smartclick, #cta-card, #free-trial + section {
      padding: 40px 20px !important;
    }
  }

  [id] { scroll-margin-top: 70px; }
`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Sanitize และ validate email
    const emailValidation = sanitizeAndValidateEmail(email);
    
    if (!emailValidation.isValid) {
      setError(getUserFriendlyError(emailValidation.error, "email"));
      inputRef.current?.focus();
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailValidation.value }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // ใช้ generic error message
        throw new Error(getUserFriendlyError(res, "submit"));
      }

      setSuccess(
        data?.message || "We have sent you a temporary password."
      );
    } catch (err) {
      setError(getUserFriendlyError(err, "submit"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{responsiveCSS}</style>
      <div
        style={{
          width: "100%",
          background: "#fff",
          overflow: "visible",
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
        }}
      >
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        {/* Hero */}
        <section style={{ background: "#0E2B55", color: "#fff", padding: "28px 24px 30px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: 0.2 }}>
              Forgot Password
            </h1>
            <p style={{ margin: "8px 0 0", color: "#93c5fd" }}>
              Enter your email and we&apos;ll send you a temporary password.
            </p>
          </div>
        </section>

        {/* Form */}
        <section style={{ background: "#F3F6FB", padding: "42px 16px 80px" }}>
          <div
            style={{
              maxWidth: 540,
              margin: "0 auto",
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #E5E7EB",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              padding: "24px 24px 28px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: "rgba(59,130,246,0.14)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#1D4ED8",
                }}
              >
                <FiSend />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#0F172A" }}>
                  Reset your password
                </div>
                <div style={{ fontSize: 13, color: "#64748B" }}>
                  We&apos;ll send a temporary password to your registered email.
                </div>
              </div>
            </div>

            <ErrorMessage message={error} style={{ marginBottom: 16 }} />
            <SuccessMessage message={success} style={{ marginBottom: 16 }} />

            <form onSubmit={handleSubmit} noValidate>
              <label style={{ display: "block", marginBottom: 14 }}>
                <div
                  style={{
                    marginBottom: 4,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0F172A",
                  }}
                >
                  Email
                </div>
                <input
                  ref={inputRef}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={254}
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #CBD5E1",
                    padding: "0 10px",
                    fontSize: 14,
                    outline: "none",
                    boxShadow: "inset 0 1px 2px rgba(15,23,42,0.05)",
                  }}
                  placeholder="Enter your registered email"
                />
              </label>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  style={{
                    height: 40,
                    padding: "0 18px",
                    borderRadius: 8,
                    border: "1px solid #CBD5E1",
                    background: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#0F172A",
                  }}
                >
                  Back to Login
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  aria-label={loading ? "Sending reset email, please wait" : "Send reset email"}
                  style={{
                    height: 40,
                    padding: "0 24px",
                    borderRadius: 8,
                    border: "1px solid #2563EB",
                    background: loading ? "#93c5fd" : "#2563EB",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    color: "#fff",
                    minWidth: 160,
                    transition: "background 0.2s ease, opacity 0.2s ease",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Sending..." : "Send Reset Email"}
                </button>
              </div>
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

// ใช้ shared components แทน - ลบ functions เดิมออกแล้ว
